import React, { useLayoutEffect } from 'react';

type Callback = (width: number, height: number, target: Element) => void;

const callbackMap = new Map<Element, Callback[]>();

const getCallbacks = (element: Element) => callbackMap.get(element) || [];

const resizeObserver =
  typeof ResizeObserver === 'function'
    ? new ResizeObserver((entries) => {
        entries.forEach(({ target, contentRect: { width, height } }) => {
          getCallbacks(target).forEach((callback) => callback(width, height, target));
        });
      })
    : undefined;

/**
 * Uses ResizeObserver to watch for changes in element dimensions
 *
 * @param elementRef A ref for the element to be observed
 * @param callback A callback to be called when a change is detected
 */
export const useResizeObserver = (
  elementRef: React.RefObject<Element | null>,
  callback: Callback,
) => {
  const targetElement = elementRef.current;
  useLayoutEffect(() => {
    // We need to access the element here rather than outside the
    // useLayoutEffect because targetElement, defined outside, may be null at
    // the time this hook is first invoked, and elementRef.current's value
    // changing (because the corresponding element mounted, most likely) will
    // not trigger another render before this useLayoutEffect is called. So it
    // will be called with a stale value of elementRef.current
    const el = elementRef.current;
    if (!el) {
      return;
    }

    // Add a callback to be called when this element changes size
    callbackMap.set(el, [...getCallbacks(el), callback]);

    return () => {
      // Remove the registered callback for the element
      const next = getCallbacks(el).filter((cb) => cb !== callback);
      if (next.length > 0) {
        callbackMap.set(el, next);
      } else {
        callbackMap.delete(el);
      }
    };
  }, [targetElement, elementRef, callback]);

  useLayoutEffect(() => {
    const el = elementRef.current;
    if (!el || !resizeObserver) {
      return;
    }

    // Have the resize observer watch this element
    resizeObserver.observe(el);

    return () => {
      resizeObserver.unobserve(el);
    };
  }, [targetElement, elementRef]);
};
